import type { ColumnDef } from "@tanstack/table-core";

import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import { simpleDateColumn } from "@/components/DataTable/Columns/simpleDateColumn";
import { simpleLinkColumn } from "@/components/DataTable/Columns/simpleLinkColumn";
import { simpleTextColumn } from "@/components/DataTable/Columns/simpleTextColumn";
import { DataTableViewOptions } from "@/components/DataTable/Table/DataTableViewOptions";

import type { ListShadowItVisitHistoriesResponse } from "../-query/type";
import { appCategoryColumn } from "../../-components/app-category-column";
import { ShadowItUser } from "../../-components/shadow-it-user";
import { traceableAppColumn } from "../../-components/traceable-app-column";

type Row = ListShadowItVisitHistoriesResponse["items"][number];

export const ColumnKey = {
  visitedAt: "visitedAt",
  user: "user",
  traceableApp: "traceableApp",
  category: "category",
  url: "url",
  detectionTool: "detectionTool",
  actions: "actions",
} as const;

export const SortableColumnKey = [ColumnKey.visitedAt] as const;

function usersColumn(): ColumnDef<Row> {
  return {
    accessorKey: ColumnKey.user,
    meta: { i18nKey: "shadowIt.user", thStyle: { minWidth: "160px" } },
    enableSorting: false,
    header: DataTableColumnHeader,
    cell: ({ getValue }) => {
      const user = getValue<Row["user"]>();

      return <ShadowItUser user={user} showDetail={true} />;
    },
  };
}

function actionsColumn(): ColumnDef<Row> {
  return {
    accessorKey: ColumnKey.actions,
    header: ({ table }) => (
      <div className="flex justify-end pr-2">
        <DataTableViewOptions table={table} />
      </div>
    ),
  };
}

export function useShadowItVisitHistoryColumns(): ColumnDef<Row>[] {
  return [
    simpleDateColumn(ColumnKey.visitedAt, "dateTimeWeekday", {
      i18nKey: "shadowIt.visitedAt",
      sortable: true,
    }),
    usersColumn(),
    traceableAppColumn(ColumnKey.traceableApp, {
      i18nKey: "shadowIt.app",
      sortable: false,
    }),
    appCategoryColumn(ColumnKey.category, (row) => row.traceableApp.category, {
      i18nKey: "shadowIt.category",
      sortable: false,
    }),
    simpleLinkColumn(ColumnKey.url, {
      i18nKey: "shadowIt.url",
      showLeadingIcon: false,
      trailingIcon: "SquareArrowOutUpRightIcon",
    }),
    simpleTextColumn(ColumnKey.detectionTool, {
      i18nKey: "shadowIt.detectionTool",
    }),
    actionsColumn(),
  ];
}
